import Breadcrumbs from "components/Common/Breadcrumb"
import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { getCouponDetails } from "store/actions/promotionsAction"
import BackButton from "components/Shared/BackButton"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { ShimmerThumbnail } from "react-shimmer-effects"
import formatDateTime from "components/Shared/formatDateTime"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import { formatImageTypeLabel } from "utils/displayLabels"

const CouponDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = t("Coupon Details ")

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType } = useSelector(state => state.Login)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      14,
      "promotion_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  const { loading, couponDetails } = useSelector(state => state.Promotions)

  useEffect(() => {
    const fetchOldDetails = () => {
      const payload = {
        id,
      }
      dispatch(getCouponDetails(payload))
    }
    fetchOldDetails()
  }, [])

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={6}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("Coupons Details")}
                </CardTitle>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(couponDetails).length === 0 ? (
                    <p className="text-center my-3">
                      {t("No coupons details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{couponDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name(English)")}</th>
                          <td>{couponDetails?.name}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name(عربي)")}</th>
                          <td>{couponDetails?.nameAr}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Description(English)")}</th>
                          <td>{couponDetails?.description || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Description(عربي)")}</th>
                          <td>{couponDetails?.descriptionAr || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Coupon Code")}</th>
                          <td>{couponDetails?.code}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Allow Infinite Usage")}</th>
                          <td>
                            {couponDetails?.allowInfiniteUsage ? "Yes" : "No"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Max Usage")}</th>
                          <td>{couponDetails?.maxUsage ?? "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Usage Count")}</th>
                          <td>{couponDetails?.usageCount}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Coupon Type")}</th>
                          <td>{t(couponDetails?.type)}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Starts At")}</th>
                          <td>
                            {formatDateTime(couponDetails?.startsAt) || "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Expires At")}</th>
                          <td>
                            {formatDateTime(couponDetails?.expiresAt) || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Image Type")}</th>
                          <td>
                            {formatImageTypeLabel(couponDetails?.imageUrlType)}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Image")}</th>
                          <td>
                            <img
                              src={couponDetails?.imageUrl}
                              alt="-"
                              className="img-fluid"
                            />
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            {couponDetails?.status === 1 ? (
                              <Badge className="bg-success">
                                {t("Active")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Deactive")}
                              </Badge>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Created By")}</th>
                          <td>
                            {couponDetails?.createdBy
                              ? `${couponDetails.createdBy.firstName || ""} ${
                                  couponDetails.createdBy.lastName || ""
                                }`.trim()
                              : "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Created")}</th>
                          <td>
                            {formatDateTime(couponDetails?.createdAt) || "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Last Updated By")}</th>
                          <td>
                            {couponDetails?.lastUpdatedBy
                              ? `${
                                  couponDetails.lastUpdatedBy.firstName || ""
                                } ${
                                  couponDetails.lastUpdatedBy.lastName || ""
                                }`.trim()
                              : "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Updated")}</th>
                          <td>
                            {formatDateTime(couponDetails?.updatedAt) || "-"}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="card_100_24">
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  {t("Discount Details")}
                </CardTitle>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(couponDetails).length === 0 ? (
                    <p className="text-center my-3">
                      {t("No discount details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Discount Name")}</th>
                          <td>{couponDetails?.promotion?.name}</td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Offer Type")}</th>
                          <td>{t(couponDetails?.promotion?.offerType)}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Amount")}</th>
                          <td>{couponDetails?.promotion?.amount ?? "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Max Amount")}</th>
                          <td>{couponDetails?.promotion?.maxAmount ?? "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Minimum Cart Amount")}</th>
                          <td>
                            {couponDetails?.promotion?.minimumCartAmount ?? "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Reusable Amount")}</th>
                          <td>
                            {couponDetails?.promotion?.reusableAmount ?? "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Discount Type")}</th>
                          <td>{couponDetails?.discountType || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Order Type")}</th>
                          <td>
                            {couponDetails?.promotion?.orderType?.map(
                              item => `${item}, `
                            ) || "-"}
                          </td>
                        </tr>

                        {couponDetails?.promotion?.limits
                          ?.isMaxUsagePerCustomer && (
                          <tr>
                            <th scope="row">{t("Max Usage Per Customer")}</th>
                            <td>
                              {couponDetails?.promotion?.limits
                                ?.maxUsagePerCustomer || "-"}
                            </td>
                          </tr>
                        )}

                        {couponDetails?.promotion?.limits
                          ?.isMaxUsageOfCustomers && (
                          <tr>
                            <th scope="row">{t("Max Usage Of Customers")}</th>
                            <td>
                              {couponDetails?.promotion?.limits
                                ?.maxUsageOfCustomers || "-"}
                            </td>
                          </tr>
                        )}

                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            {couponDetails?.promotion?.status === 1 ? (
                              <Badge className="bg-success">
                                {t("Active")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Deactive")}
                              </Badge>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Created At")}</th>
                          <td>
                            {formatDateTime(
                              couponDetails?.promotion?.createdAt
                            ) || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Updated At")}</th>
                          <td>
                            {formatDateTime(
                              couponDetails?.promotion?.updatedAt
                            ) || "-"}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default CouponDetails
